'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { organizations, projects } from '@/lib/api';
import { Building2, Plus, ArrowRight, FolderKanban, Shield, Sparkles, LayoutDashboard, Calendar, Trash2 } from 'lucide-react';
import { DashboardSkeleton } from '@/components/ui/Skeleton';
import type { User, OrganizationWithCount } from '@/types';

interface Project {
  id: string;
  name: string;
  description: string | null;
  organizationId: string;
  framework: string;
  createdAt: string;
  latestScan?: {
    id: string;
    overallCompliance: number;
    sovereigntyScore: number;
    generatedAt: string;
  } | null;
  liveScore?: number | null; // Live calculated score when no saved scan exists
  _count?: {
    assets: number;
    scanReports: number;
  };
}

interface ProjectWithOrg extends Project {
  organizationName: string;
}

// Country flag helper
const getCountryFlag = (country: string): string => {
  const flags: Record<string, string> = {
    'Germany': '🇩🇪', 'France': '🇫🇷', 'Netherlands': '🇳🇱', 'Belgium': '🇧🇪',
    'Austria': '🇦🇹', 'Switzerland': '🇨🇭', 'Spain': '🇪🇸', 'Italy': '🇮🇹',
    'Poland': '🇵🇱', 'Portugal': '🇵🇹', 'Sweden': '🇸🇪', 'Finland': '🇫🇮',
    'Norway': '🇳🇴', 'Denmark': '🇩🇰', 'Ireland': '🇮🇪', 'Luxembourg': '🇱🇺',
    'DE': '🇩🇪', 'FR': '🇫🇷', 'NL': '🇳🇱', 'BE': '🇧🇪', 'AT': '🇦🇹', 'CH': '🇨🇭',
    'ES': '🇪🇸', 'IT': '🇮🇹', 'PL': '🇵🇱', 'PT': '🇵🇹', 'SE': '🇸🇪', 'FI': '🇫🇮',
  };
  return flags[country] || '🇪🇺';
};

// Sector icon helper
const getSectorIcon = (sector: string) => {
  const icons: Record<string, string> = {
    'technology': '💻', 'finance': '🏦', 'healthcare': '🏥', 'education': '🎓',
    'retail': '🛒', 'manufacturing': '🏭', 'services': '🤝', 'government': '🏛️',
    'legal': '⚖️', 'media': '📺', 'energy': '⚡', 'real estate': '🏢',
  };
  return icons[sector?.toLowerCase()] || '🏢';
};

// Organization size tag helper
const getOrgSizeTag = (projectCount: number) => {
  if (projectCount === 0) return { label: 'New', color: 'bg-gray-100 text-gray-600' };
  if (projectCount <= 2) return { label: 'Starter', color: 'bg-blue-100 text-blue-700' };
  if (projectCount <= 5) return { label: 'Growing', color: 'bg-green-100 text-green-700' };
  return { label: 'Enterprise', color: 'bg-purple-100 text-purple-700' };
};

// Organization completeness helper
const getOrgCompleteness = (org: OrganizationWithCount) => {
  let score = 0;
  if (org.name) score += 25;
  if (org.sector) score += 25;
  if (org.country) score += 25;
  if ((org._count?.projects || 0) > 0) score += 25;
  return score;
};

// Get status color based on completeness/score
const getStatusColor = (score: number) => {
  if (score >= 75) return { border: 'border-green-300', bg: 'from-green-50 to-emerald-50', indicator: 'bg-green-500' };
  if (score >= 50) return { border: 'border-yellow-300', bg: 'from-yellow-50 to-amber-50', indicator: 'bg-yellow-500' };
  return { border: 'border-red-300', bg: 'from-red-50 to-rose-50', indicator: 'bg-red-500' };
};

// Project type tag helper (based on description keywords)
const getProjectTypeTag = (description: string | null) => {
  const desc = (description || '').toLowerCase();
  if (desc.includes('api') || desc.includes('backend')) return { label: 'API', color: 'bg-indigo-100 text-indigo-700' };
  if (desc.includes('web') || desc.includes('frontend')) return { label: 'Web App', color: 'bg-cyan-100 text-cyan-700' };
  if (desc.includes('mobile') || desc.includes('app')) return { label: 'Mobile', color: 'bg-pink-100 text-pink-700' };
  if (desc.includes('data') || desc.includes('analytics')) return { label: 'Data', color: 'bg-orange-100 text-orange-700' };
  return { label: 'General', color: 'bg-gray-100 text-gray-600' };
};

// Framework tag helper
const getFrameworkTag = (framework: string | null | undefined) => {
  const fw = (framework || '').toUpperCase();
  const frameworkMap: Record<string, { label: string; color: string; icon: string }> = {
    'GDPR': { label: 'GDPR', color: 'bg-blue-100 text-blue-700', icon: '🔒' },
    'NIS2': { label: 'NIS2', color: 'bg-purple-100 text-purple-700', icon: '🛡️' },
    'DORA': { label: 'DORA', color: 'bg-green-100 text-green-700', icon: '⚡' },
    'EPRIVACY': { label: 'ePrivacy', color: 'bg-orange-100 text-orange-700', icon: '🍪' },
    'AI_ACT': { label: 'AI Act', color: 'bg-pink-100 text-pink-700', icon: '🤖' },
    'ISO27001': { label: 'ISO 27001', color: 'bg-indigo-100 text-indigo-700', icon: '📋' },
    'SOC2': { label: 'SOC 2', color: 'bg-cyan-100 text-cyan-700', icon: '✓' },
    'HIPAA': { label: 'HIPAA', color: 'bg-red-100 text-red-700', icon: '🏥' },
    'PCI_DSS': { label: 'PCI DSS', color: 'bg-amber-100 text-amber-700', icon: '💳' },
  };
  return frameworkMap[fw] || { label: fw || 'Unknown', color: 'bg-gray-100 text-gray-600', icon: '📄' };
};

// Compliance score color helper
const getComplianceScoreStyle = (score: number | null | undefined) => {
  if (score === null || score === undefined) return { color: 'text-gray-400', bg: 'bg-gray-100', label: 'No scan' };
  if (score >= 80) return { color: 'text-green-600', bg: 'bg-green-100', label: 'Excellent' };
  if (score >= 60) return { color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Good' };
  if (score >= 40) return { color: 'text-orange-600', bg: 'bg-orange-100', label: 'Fair' };
  return { color: 'text-red-600', bg: 'bg-red-100', label: 'Needs Work' };
};

export default function DashboardPage() {
  const [user, setUser] = useState<User | null>(null);
  const [orgList, setOrgList] = useState<OrganizationWithCount[]>([]);
  const [projectList, setProjectList] = useState<ProjectWithOrg[]>([]);
  const [loading, setLoading] = useState(true);
  // Delete modal state
  const [deletingOrg, setDeletingOrg] = useState<OrganizationWithCount | null>(null);
  const [deletingProject, setDeletingProject] = useState<ProjectWithOrg | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch {
        // Ignore parse errors
      }
    }
    
    loadData();
  }, []);

  const loadData = async () => {
    // First load organizations
    const orgResult = await organizations.list();
    if (orgResult.data && Array.isArray(orgResult.data)) {
      setOrgList(orgResult.data);
      
      // Then load projects for each organization
      const allProjects: ProjectWithOrg[] = [];
      for (const org of orgResult.data) {
        const projectResult = await projects.list(org.id);
        if (projectResult.data && Array.isArray(projectResult.data)) {
          const projectsWithOrg = (projectResult.data as Project[]).map(p => ({
            ...p,
            organizationName: org.name
          }));
          allProjects.push(...projectsWithOrg);
        }
      }
      // Sort by most recent first
      allProjects.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setProjectList(allProjects);
    }
    setLoading(false);
  };

  const handleDeleteOrg = async () => {
    if (!deletingOrg) return;
    setDeleteLoading(true);
    const result = await organizations.delete(deletingOrg.id);
    setDeleteLoading(false);
    if (!result.error) {
      setDeletingOrg(null);
      loadData();
    } else {
      alert(result.error);
    }
  };

  const handleDeleteProject = async () => {
    if (!deletingProject) return;
    setDeleteLoading(true);
    const result = await projects.delete(deletingProject.id);
    setDeleteLoading(false);
    if (!result.error) {
      setDeletingProject(null);
      loadData();
    } else {
      alert(result.error);
    }
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  const totalProjects = orgList.reduce((sum, org) => sum + (org._count?.projects || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with gradient background */}
        <div className="relative mb-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 opacity-10 rounded-2xl"></div>
          <div className="relative px-6 py-8 rounded-2xl border border-blue-100 bg-white/60 backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg shadow-blue-500/25">
                <LayoutDashboard className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-900 bg-clip-text text-transparent">
                  Dashboard
                </h1>
                <p className="text-gray-600 text-sm">
                  {user?.name ? `Welcome back, ${user.name}!` : 'Overview of your compliance management'}
                </p>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-200/50">
              <span className="text-sm text-gray-500 font-medium mr-2">Quick Actions:</span>
              <Link
                href="/organizations/new"
                className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-all"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                New Organization
              </Link>
              {orgList.length > 0 && (
                <Link
                  href={`/projects/new?org=${orgList[0].id}`}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-blue-500/25 transition-all"
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  New Project
                </Link>
              )}
              <Link
                href="/dashboard/organizations"
                className="inline-flex items-center px-4 py-2 text-blue-600 hover:text-blue-700 text-sm font-medium hover:bg-blue-50 rounded-lg transition-all ml-auto"
              >
                View All Organizations
                <ArrowRight className="h-4 w-4 ml-1.5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="group bg-white rounded-2xl border-2 border-gray-100 p-6 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Organizations</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{orgList.length}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl group-hover:scale-110 transition-transform duration-300">
                <Building2 className="h-7 w-7 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <Link href="/dashboard/organizations" className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                Manage organizations <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>

          <div className="group bg-white rounded-2xl border-2 border-gray-100 p-6 hover:border-green-300 hover:shadow-xl hover:shadow-green-500/10 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Total Projects</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">{totalProjects}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl group-hover:scale-110 transition-transform duration-300">
                <FolderKanban className="h-7 w-7 text-green-600" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <span className="text-sm text-gray-500">Across all organizations</span>
            </div>
          </div>

          <div className="group bg-white rounded-2xl border-2 border-gray-100 p-6 hover:border-purple-300 hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Compliance</p>
                <p className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
                  {totalProjects > 0 ? 'Active' : '-'}
                </p>
              </div>
              <div className="p-3 bg-gradient-to-br from-purple-50 to-violet-100 rounded-xl group-hover:scale-110 transition-transform duration-300">
                <Shield className="h-7 w-7 text-purple-600" />
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <Link href="/alternatives" className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1">
                View EU alternatives <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Organizations */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-blue-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg shadow-blue-500/25">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Organizations</h2>
                  <p className="text-sm text-gray-600">Manage your compliance organizations</p>
                </div>
              </div>
              <Link
                href="/organizations/new"
                className="group inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-blue-500/25 hover:scale-105 transition-all"
              >
                <Plus className="h-5 w-5 mr-2" />
                New Organization
                <ArrowRight className="h-4 w-4 ml-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </Link>
            </div>
          </div>

          <div className="p-8">
            {orgList.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-blue-200 rounded-2xl bg-gradient-to-br from-blue-50/50 to-indigo-50/50">
                <div className="relative inline-block mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl blur-2xl opacity-20"></div>
                  <div className="relative p-6 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl">
                    <Building2 className="h-14 w-14 text-blue-600" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">No Organizations Yet</h3>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                  Create your first organization to start managing compliance and digital sovereignty
                </p>
                <Link
                  href="/organizations/new"
                  className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-xl hover:shadow-blue-500/25 hover:scale-105 transition-all"
                >
                  <Sparkles className="h-5 w-5 mr-2" />
                  Create Your First Organization
                  <ArrowRight className="h-5 w-5 ml-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </div>
            ) : (
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {orgList.map((org) => {
                  const completeness = getOrgCompleteness(org);
                  const statusColor = getStatusColor(completeness);
                  const sizeTag = getOrgSizeTag(org._count?.projects || 0);
                  
                  return (
                  <div
                    key={org.id}
                    className={`group relative bg-gradient-to-br ${completeness >= 75 ? 'from-slate-50 to-blue-50' : completeness >= 50 ? 'from-slate-50 to-amber-50' : 'from-slate-50 to-red-50'} rounded-2xl border-2 ${completeness < 75 ? statusColor.border : 'border-gray-100 hover:border-blue-300'} transition-all duration-300 overflow-hidden hover:shadow-xl hover:shadow-blue-500/10`}
                  >
                    {/* Status indicator bar */}
                    <div className={`absolute top-0 left-0 right-0 h-1 ${statusColor.indicator}`} />
                    
                    <Link href={`/organizations/${org.id}`} className="block p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="p-2.5 bg-white rounded-xl shadow-sm group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                          <Building2 className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Score indicator */}
                          <span className={`inline-flex items-center px-2 py-0.5 ${statusColor.indicator} text-white rounded text-xs font-bold`}>
                            {completeness}%
                          </span>
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                            <FolderKanban className="h-3.5 w-3.5" />
                            {org._count?.projects || 0}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-700 transition-colors">
                        {org.name}
                      </h3>
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className={`inline-flex items-center px-2.5 py-1 ${sizeTag.color} rounded-full text-xs font-medium`}>
                          {sizeTag.label}
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                          <span>{getSectorIcon(org.sector)}</span>
                          {org.sector}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 text-gray-700 rounded-full text-xs font-medium">
                          <span>{getCountryFlag(org.country)}</span>
                          {org.country}
                        </span>
                      </div>
                      {completeness < 75 && (
                        <p className="text-xs text-amber-600 mb-2">
                          Profile incomplete - add more details
                        </p>
                      )}
                    </Link>
                    <div className="px-5 pb-5 pt-3 border-t border-gray-200 flex items-center justify-between">
                      <Link href={`/organizations/${org.id}`} className="flex items-center text-blue-600 text-sm font-medium hover:text-blue-700 transition-colors">
                        View Details
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Link>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDeletingOrg(org);
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete organization"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Projects Section */}
        {projectList.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mt-8">
            <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg shadow-green-500/25">
                    <FolderKanban className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Recent Projects</h2>
                    <p className="text-sm text-gray-600">{projectList.length} project{projectList.length !== 1 ? 's' : ''} across all organizations</p>
                  </div>
                </div>
                <Link
                  href={`/projects/new?org=${orgList[0]?.id || ''}`}
                  className="group inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-green-500/25 hover:scale-105 transition-all"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  New Project
                  <ArrowRight className="h-4 w-4 ml-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              </div>
            </div>

            <div className="p-8">
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {projectList.slice(0, 6).map((project) => {
                  const typeTag = getProjectTypeTag(project.description);
                  const frameworkTag = getFrameworkTag(project.framework);
                  // Use saved scan score, or live score, or null
                  const complianceScore = project.latestScan?.overallCompliance ?? project.liveScore ?? null;
                  const scoreStyle = getComplianceScoreStyle(complianceScore);
                  const isLiveScore = !project.latestScan && project.liveScore !== null && project.liveScore !== undefined;
                  // Calculate days since creation for a "freshness" indicator
                  const daysOld = Math.floor((Date.now() - new Date(project.createdAt).getTime()) / (1000 * 60 * 60 * 24));
                  const isNew = daysOld < 7;
                  
                  return (
                  <div
                    key={project.id}
                    className="group relative bg-gradient-to-br from-slate-50 to-green-50 rounded-xl border-2 border-gray-100 hover:border-green-300 transition-all duration-300 overflow-hidden hover:shadow-lg hover:shadow-green-500/10"
                  >
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500" />
                    
                    <Link href={`/projects/${project.id}`} className="block p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform duration-300">
                            <FolderKanban className="h-5 w-5 text-green-600" />
                          </div>
                          {isNew && (
                            <span className="px-1.5 py-0.5 bg-green-500 text-white rounded text-[10px] font-bold uppercase">
                              New
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          {/* Framework Tag */}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 ${frameworkTag.color} rounded text-xs font-medium`}>
                            <span className="text-[10px]">{frameworkTag.icon}</span>
                            {frameworkTag.label}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-green-700 transition-colors truncate">
                        {project.name}
                      </h3>
                      {project.description ? (
                        <p className="text-sm text-gray-500 mb-3 line-clamp-1">{project.description}</p>
                      ) : (
                        <p className="text-sm text-amber-500 mb-3 italic">No description yet</p>
                      )}
                      
                      {/* Compliance Score Display */}
                      <div className={`flex items-center justify-between p-2 ${scoreStyle.bg} rounded-lg mb-3`}>
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-gray-500" />
                          <span className="text-xs font-medium text-gray-600">Compliance Score</span>
                          {isLiveScore && (
                            <span className="px-1.5 py-0.5 bg-blue-500 text-white rounded text-[9px] font-bold uppercase">
                              Live
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {complianceScore !== undefined && complianceScore !== null ? (
                            <>
                              <span className={`text-lg font-bold ${scoreStyle.color}`}>
                                {Math.round(complianceScore)}%
                              </span>
                              <span className={`text-[10px] font-medium ${scoreStyle.color} opacity-70`}>
                                {scoreStyle.label}
                              </span>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400 italic">
                              {(project._count?.assets || 0) > 0 ? 'Calculating...' : 'Add assets first'}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(project.createdAt).toLocaleDateString()}
                        </span>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 rounded">
                          <Building2 className="h-3 w-3" />
                          {project.organizationName}
                        </span>
                      </div>
                    </Link>
                    <div className="px-4 pb-4 pt-3 border-t border-gray-200 flex items-center justify-between">
                      <Link href={`/projects/${project.id}`} className="flex items-center text-green-600 text-sm font-medium hover:text-green-700 transition-colors">
                        Open Project
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Link>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDeletingProject(project);
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete project"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
              {projectList.length > 6 && (
                <div className="mt-6 text-center">
                  <Link
                    href="/dashboard/organizations"
                    className="inline-flex items-center text-green-600 hover:text-green-700 font-medium"
                  >
                    View all {projectList.length} projects
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Organization Modal */}
      {deletingOrg && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-red-50 to-rose-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-xl">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Delete Organization</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete <strong>{deletingOrg.name}</strong>?
              </p>
              <p className="text-sm text-red-600 mb-6 bg-red-50 p-3 rounded-lg">
                This will permanently delete the organization and all associated projects, assets, and compliance data.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingOrg(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteOrg}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Project Modal */}
      {deletingProject && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-red-50 to-rose-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-xl">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Delete Project</h3>
                  <p className="text-sm text-gray-600">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete <strong>{deletingProject.name}</strong>?
              </p>
              <p className="text-sm text-red-600 mb-6 bg-red-50 p-3 rounded-lg">
                This will permanently delete the project and all associated assets, compliance records, and scan data.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDeletingProject(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteProject}
                  disabled={deleteLoading}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleteLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
